import type { BasePageProps } from '@shared/components/BasePageProps';
import type { OfferingProperties } from '@shared/domain/offering/offering.types';
import { Category } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from '@shared/components/Buttons/Button';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { Page } from '@shared/components/Page';
import { OfferingStatus, OfferingType } from '@shared/domain/offering/offering.types';
import { useApi } from '@shared/services/api';
import { log } from '@shared/utils/log';
import { capitalize } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import Input from '@/shared/components/Input';
import { UserContext } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import TrialExpiredPaywall from '../../company/company-profile-page/components/TrialExpiredPaywall';
import ArrayFieldEditor from './ArrayFieldEditor';
import AutoGenerateOfferingModal from './AutoGenerateOfferingModal';

export interface GeneratedOfferingData {
  name: string;
  type: 'product' | 'service';
  description: string;
  buyerRoles: string[];
  differentiators: string[];
  icps: string[];
  problemsSolved: string[];
  useCases: string[];
}

interface OfferingsPageProps extends BasePageProps {}

function OfferingsPage(props: OfferingsPageProps) {
  const { embedded = false } = props;
  const api = useApi();
  const { openMessage } = useMessageService();

  const [offering, setOffering] = useState<OfferingProperties | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAutoGenerateModalOpen, setIsAutoGenerateModalOpen] = useState(false);
  const { user, isLoggedIn } = useContext(UserContext);
  const accountId = user?.accountMemberships?.[0]?.accountId;
  const isContributor = user?.planSlug === 'contributor';
  const isTrialExpired = user?.subscriptionStatus === 'trial_expired' || isContributor;

  const [formData, setFormData] = useState<Partial<OfferingProperties>>({
    name: '',
    type: OfferingType.PRODUCT,
    status: OfferingStatus.ACTIVE,
    description: '',
    buyerRoles: [],
    differentiators: [],
    icps: [],
    problemsSolved: [],
    useCases: [],
  });

  const fetchOffering = async () => {
    if (!isLoggedIn || !accountId || isTrialExpired) {
      if (isTrialExpired) setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/account/${accountId}`);
      if (response.error) {
        setError(response.message);
        return;
      }

      const offerings = response.data?.offerings || [];
      if (offerings.length > 0) {
        setOffering(offerings[0]);
        setFormData(offerings[0]);
      } else {
        setOffering(null);
      }
    } catch (err) {
      log.error('Error fetching offering:', err);
      setError('Failed to load offering');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffering();
  }, [accountId]);

  const handleEdit = () => {
    if (offering) {
      setFormData(offering);
      setIsEditMode(true);
      setError(null);
      setSuccess(null);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      type: OfferingType.PRODUCT,
      status: OfferingStatus.ACTIVE,
      description: '',
      buyerRoles: [],
      differentiators: [],
      icps: [],
      problemsSolved: [],
      useCases: [],
    });
    setIsCreating(true);
    setError(null);
    setSuccess(null);
  };

  const handleAutoGenerateSuccess = (generatedOffering: GeneratedOfferingData) => {
    setFormData({
      name: generatedOffering.name,
      type: generatedOffering.type === 'product' ? OfferingType.PRODUCT : OfferingType.SERVICE,
      status: OfferingStatus.ACTIVE,
      description: generatedOffering.description,
      buyerRoles: generatedOffering.buyerRoles || [],
      differentiators: generatedOffering.differentiators || [],
      icps: generatedOffering.icps || [],
      problemsSolved: generatedOffering.problemsSolved || [],
      useCases: generatedOffering.useCases || [],
    });
    setIsCreating(true);
    setError(null);
    setSuccess('Offering details generated! Review and save when ready.');
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setIsCreating(false);
    if (offering) {
      setFormData(offering);
    }
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!formData.name || formData.name.trim().length === 0) {
      setError('Offering name is required');
      return;
    }

    if (!formData.description || formData.description.trim().length === 0) {
      setError('Offering description is required');
      return;
    }

    try {
      if (isCreating) {
        const response = await api.post(`/account/${accountId}/offerings`, {
          ...formData,
          accountId,
        });

        if (response.error) {
          setError(response.message);
          return;
        }

        setOffering(response.data);
        setFormData(response.data);
        setSuccess('Offering created successfully!');
        setIsCreating(false);
      } else {
        const response = await api.put(`/account/${accountId}/offerings/${formData.id}`, {
          ...formData,
          accountId,
        });

        if (response.error) {
          setError(response.message);
          return;
        }
        setSuccess('Offering updated successfully!');
        setOffering(formData as OfferingProperties);
        setIsEditMode(false);
      }
    } catch {
      setError('Failed to save offering');
    }
  };

  const handleDelete = () => {
    if (!offering?.id) {
      return;
    }

    openMessage({
      modalType: 'action',
      header: 'Delete Offering',
      customBody: (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          Are you sure you want to delete this offering? This action cannot be undone.
        </Typography>
      ),
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmButtonColor: 'error',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/account/${accountId}/offerings/${offering.id}`);

          if (response.error) {
            setError(response.message);
            return;
          }

          setSuccess('Offering deleted successfully!');
          setOffering(null);
          setIsEditMode(false);
          setFormData({
            name: '',
            type: OfferingType.PRODUCT,
            status: OfferingStatus.DRAFT,
            description: '',
            buyerRoles: [],
            differentiators: [],
            icps: [],
            problemsSolved: [],
            useCases: [],
          });
        } catch (err) {
          log.error('Error deleting offering:', err);
          setError('Failed to delete offering');
        }
      },
    });
  };

  const handleFieldChange = (field: keyof OfferingProperties, value: any) => {
    setFormData((prev: Partial<OfferingProperties>) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addArrayItem = (field: keyof OfferingProperties, value: string) => {
    if (value.trim()) {
      const currentArray = (formData[field] as string[]) || [];
      handleFieldChange(field, [...currentArray, value.trim()]);
    }
  };

  const removeArrayItem = (field: keyof OfferingProperties, index: number) => {
    const currentArray = (formData[field] as string[]) || [];
    handleFieldChange(
      field,
      currentArray.filter((_, i) => i !== index),
    );
  };

  if (loading) {
    return <LoadingSpinner variant="circular" size="large" message="Loading offerings..." fullScreen />;
  }

  if (isTrialExpired) {
    return (
      <Page
        icon={Category}
        title="Offerings"
        subtitle="Describe your company's products and services here.  This will will help Kandir provide more contextual insights and recommendations."
        embedded={embedded}
      >
        <TrialExpiredPaywall standalone isContributor={isContributor} />
      </Page>
    );
  }

  const isViewMode = !isEditMode && !isCreating;

  return (
    <Page
      icon={Category}
      title="Offerings"
      subtitle="Describe your company's products and services here.  This will will help Kandir provide more contextual insights and recommendations."
      embedded={embedded}
    >
      {error && (
        <Alert severity="error" className="mb-16" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className="mb-16" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!offering && !isCreating ? (
        <Card className="offering-empty-card">
          <CardContent>
            <div className="offering-empty-card__title">Create Your First Offering</div>
            <div className="offering-empty-card__subtitle">
              Define your company's offerings to unlock tailored insights and recommendations.
            </div>
            <div className="offering-empty-card__actions">
              <Tooltip title="Let AI analyze your website and fill in the details automatically">
                <span>
                  <Button
                    data-testid="auto-generate-offering-button"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setIsAutoGenerateModalOpen(true)}
                    variantType="addButton"
                  >
                    Auto-Generate
                  </Button>
                </span>
              </Tooltip>
              <Button data-testid="create-offering-button" startIcon={<AddIcon />} onClick={handleCreate} whiteTheme>
                Create Manually
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="offering-card">
          <CardContent className="offering-card__content">
            <div className="d-flex mb-24">
              <div style={{ flex: 1 }}>
                {isViewMode ? (
                  <>
                    <div className="offering-field__label">Name</div>
                    <div className="offering-field__value">{formData.name}</div>
                  </>
                ) : (
                  <Input
                    testId="offering-name-input"
                    name="name"
                    label="Offering Name"
                    currentValue={formData.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    required
                    fullWidth
                  />
                )}
              </div>
              {isViewMode && (
                <div className="d-flex" style={{ gap: '16px', justifyContent: 'flex-end' }}>
                  <Button data-testid="edit-offering-button" startIcon={<EditIcon />} onClick={handleEdit} whiteTheme>
                    Edit
                  </Button>
                </div>
              )}

              {(isEditMode || isCreating) && (
                <div className="d-flex" style={{ gap: '16px', justifyContent: 'flex-end' }}>
                  {isEditMode && (
                    <Button
                      data-testid="delete-offering-button"
                      startIcon={<DeleteIcon />}
                      size="small"
                      onClick={handleDelete}
                      variantType="error"
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    data-testid="cancel-edit-offering-button"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    whiteTheme
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="save-offering-button"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    variantType="success"
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
            <div className="d-flex mb-24" style={{ gap: '16px' }}>
              <div style={{ flex: 1 }}>
                {isViewMode ? (
                  <>
                    <div className="offering-field__label">Type</div>
                    <div className="offering-field__value">{formData.type}</div>
                  </>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      data-testid="offering-type-select"
                      value={formData.type || 'product'}
                      label="Type"
                      onChange={(e) => handleFieldChange('type', e.target.value)}
                    >
                      <MenuItem value="product">Product</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </div>

              <div style={{ flex: 1 }}>
                {isViewMode ? (
                  <>
                    <div className="offering-field__label">Status</div>
                    <div className="offering-field__value">{formData.status}</div>
                  </>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      data-testid="offering-status-select"
                      value={formData.status || 'active'}
                      label="Status"
                      onChange={(e) => handleFieldChange('status', e.target.value)}
                    >
                      {Object.values(OfferingStatus).map((status) => (
                        <MenuItem data-testid={`offering-status-option-${status}`} key={status} value={status}>
                          {capitalize(status)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
            </div>

            <div className="mb-24">
              {isViewMode ? (
                <>
                  <div className="offering-field__label">Description</div>
                  <div className="offering-field__value">{formData.description || 'No description provided'}</div>
                </>
              ) : (
                <Input
                  testId="offering-description-input"
                  name="description"
                  label="Description"
                  currentValue={formData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  required
                  fullWidth
                />
              )}
            </div>

            <ArrayFieldEditor
              label="Buyer Roles"
              items={formData.buyerRoles || []}
              isViewMode={isViewMode}
              onAdd={(value) => addArrayItem('buyerRoles', value)}
              onRemove={(index) => removeArrayItem('buyerRoles', index)}
              itemType="chip"
              placeholder="Add buyer role"
              helpText='Who are the key decision makers and stakeholders involved in purchasing your offering? (e.g., "CTO", "VP Sales", "Director of Marketing"). List each separately.'
              testIdPrefix="buyer-role"
            />

            <ArrayFieldEditor
              label="Differentiators"
              items={formData.differentiators || []}
              isViewMode={isViewMode}
              onAdd={(value) => addArrayItem('differentiators', value)}
              onRemove={(index) => removeArrayItem('differentiators', index)}
              itemType="list"
              placeholder="Add differentiator"
              helpText="What makes your offering unique compared to alternatives? List each separately."
              multiline
              testIdPrefix="differentiator"
            />

            <ArrayFieldEditor
              label="Ideal Customer Profiles"
              items={formData.icps || []}
              isViewMode={isViewMode}
              onAdd={(value) => addArrayItem('icps', value)}
              onRemove={(index) => removeArrayItem('icps', index)}
              itemType="list"
              placeholder="Add ICP"
              helpText="Describe your perfect customers. What industries, company sizes, or characteristics make them ideal buyers? List each separately."
              multiline
              testIdPrefix="icp"
            />

            <ArrayFieldEditor
              label="Problems Solved"
              items={formData.problemsSolved || []}
              isViewMode={isViewMode}
              onAdd={(value) => addArrayItem('problemsSolved', value)}
              onRemove={(index) => removeArrayItem('problemsSolved', index)}
              itemType="list"
              placeholder="Add problem solved"
              helpText="What specific pain points, challenges, or business problems does your offering address or solve? List each separately."
              multiline
              testIdPrefix="problem-solved"
            />

            <ArrayFieldEditor
              label="Use Cases"
              items={formData.useCases || []}
              isViewMode={isViewMode}
              onAdd={(value) => addArrayItem('useCases', value)}
              onRemove={(index) => removeArrayItem('useCases', index)}
              itemType="list"
              placeholder="Add use case"
              helpText="How do customers typically use your offering? What are the common scenarios or applications? List each separately."
              multiline
              testIdPrefix="use-case"
            />
          </CardContent>
        </Card>
      )}

      <AutoGenerateOfferingModal
        open={isAutoGenerateModalOpen}
        onClose={() => setIsAutoGenerateModalOpen(false)}
        onSuccess={handleAutoGenerateSuccess}
      />
    </Page>
  );
}

export default OfferingsPage;
